import { useState, useCallback } from 'react';
import { StatusType } from '@/components/ui/status-modal';

export interface StatusModalState {
  isOpen: boolean;
  title: string;
  message: string;
  status: StatusType;
  progress?: number;
  showProgress?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  };
  autoClose?: boolean;
  autoCloseDelay?: number;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const initialState: StatusModalState = {
  isOpen: false,
  title: '',
  message: '',
  status: 'info',
  showProgress: false,
  autoClose: false,
  autoCloseDelay: 3000,
  showCloseButton: true,
  size: 'md'
};

export function useStatusModal() {
  const [modalState, setModalState] = useState<StatusModalState>(initialState);

  const openModal = useCallback((config: Partial<StatusModalState>) => {
    setModalState({
      ...initialState,
      ...config,
      isOpen: true
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const updateProgress = useCallback((progress: number) => {
    setModalState(prev => ({ ...prev, progress }));
  }, []);

  const updateStatus = useCallback((status: StatusType, message?: string) => {
    setModalState(prev => ({
      ...prev,
      status,
      ...(message && { message })
    }));
  }, []);

  // Convenience methods for common scenarios
  const showLoading = useCallback((title: string, message: string, showProgress = false) => {
    openModal({
      title,
      message,
      status: 'loading',
      showProgress,
      showCloseButton: false,
      autoClose: false
    });
  }, [openModal]);

  const showSuccess = useCallback((title: string, message: string, autoClose = true) => {
    openModal({
      title,
      message,
      status: 'success',
      autoClose,
      autoCloseDelay: 2000
    });
  }, [openModal]);

  const showError = useCallback((title: string, message: string, primaryAction?: StatusModalState['primaryAction']) => {
    openModal({
      title,
      message,
      status: 'error',
      primaryAction,
      autoClose: false
    });
  }, [openModal]);

  const showWarning = useCallback((title: string, message: string, primaryAction?: StatusModalState['primaryAction']) => {
    openModal({
      title,
      message,
      status: 'warning',
      primaryAction,
      autoClose: false
    });
  }, [openModal]);

  const showInfo = useCallback((title: string, message: string, autoClose = true) => {
    openModal({
      title,
      message,
      status: 'info',
      autoClose,
      autoCloseDelay: 3000
    });
  }, [openModal]);

  // Session expiration specific method
  const showSessionExpired = useCallback((onLoginClick: () => void, onStayLoggedIn?: () => void) => {
    openModal({
      title: 'Session Expired',
      message: 'Your session has expired for security reasons. Please log in again to continue.',
      status: 'warning',
      primaryAction: {
        label: 'Stay Logged In',
        onClick: onStayLoggedIn || (() => {}),
        variant: 'default'
      },
      secondaryAction: {
        label: 'Log In Again',
        onClick: () => {
          onLoginClick();
        },
        variant: 'default'
      },
      autoClose: false,
      showCloseButton: false,
      size: 'md'
    });
  }, [openModal]);

  // Idle timeout warning method - allows user to stay logged in
  const showIdleWarning = useCallback((onStayLoggedIn: () => void, onLogout: () => void) => {
    openModal({
      title: 'Session Timeout Warning',
      message: "You've been inactive for a while. You'll be logged out in 60 seconds for security reasons.",
      status: 'warning',
      primaryAction: {
        label: 'Stay Logged In',
        onClick: onStayLoggedIn,
        variant: 'default'
      },
      secondaryAction: {
        label: 'Log Out Now',
        onClick: onLogout,
        variant: 'outline'
      },
      autoClose: false,
      showCloseButton: false,
      size: 'md'
    });
  }, [openModal]);

  // Export progress specific method
  const showExportProgress = useCallback((onCancel?: () => void) => {
    openModal({
      title: 'Exporting Data',
      message: 'Please wait while we prepare your export file...',
      status: 'loading',
      showProgress: true,
      progress: 0,
      showCloseButton: false,
      autoClose: false,
      ...(onCancel && {
        secondaryAction: {
          label: 'Cancel',
          onClick: onCancel,
          variant: 'outline'
        }
      })
    });
  }, [openModal]);

  return {
    modalState,
    openModal,
    closeModal,
    updateProgress,
    updateStatus,
    showLoading,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showSessionExpired,
    showIdleWarning,
    showExportProgress
  };
}
